import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  SafeAreaView,
  StyleSheet,
  View,
  Text,
  FlatList,
  TouchableOpacity,
} from 'react-native';
import { Icon } from 'react-native-elements';
import SearchBar from '../../components/SearchBar';
import { BuildingSelectionProps } from '../../navigation';
import config from '../../lib/config';
import { RootState } from '../../store/rootReducer';
import { getTopImageUrl } from '../../lib/gallery';

import { getGalleryList, selectGallery } from '../../store/gallerySlice';
import { Gallery } from '../../api/adserver';
import { showModal, hideModal } from '../../store/appSlice';
import { ModalName } from '../../components/RootOverlays';
import ZTImage from '../../components/ZTImage';
import { get } from 'lodash';

type Props = {} & BuildingSelectionProps;

const keyExtractor = (item: Gallery) => item.id.toString();

const GalleryScreen: React.FC<Props> = ({ navigation, route }) => {
  const dispatch = useDispatch();

  const { contentId } = useMemo(() => {
    return {
      contentId: get(route, 'params.contentId', ''),
    };
  }, [route]);
  console.log(`GalleryScreen, contentId: ${contentId}`);

  const data = useSelector((state) => selectGallery(state as RootState).data);

  useEffect(() => {
    dispatch(getGalleryList());
  }, [dispatch]);

  const handleOnClose = useCallback(() => {
    dispatch(hideModal());
  }, [dispatch]);

  const onViewDetail = useCallback(
    (item: Gallery) => {
      dispatch(
        showModal({
          isShowModal: true,
          modalName: ModalName.Gallery,
          modalProps: {
            item,
            onClose: handleOnClose,
          },
        }),
      );
    },
    [dispatch, handleOnClose],
  );

  useEffect(() => {
    if (contentId) {
      const newsItem = data.find((item) => String(item.id) === contentId);
      if (newsItem) {
        onViewDetail(newsItem);
      }
    }
  }, [contentId, data, onViewDetail]);

  const renderItem = useCallback(
    ({ item }) => (
      <TouchableOpacity onPress={() => onViewDetail(item)}>
        <View style={styles.Gallery}>
          <View style={styles.content}>
            <ZTImage
              uri={getTopImageUrl(item) || ''}
              style={styles.mainImage}
            />

            <Text style={styles.title}>{item.title}</Text>
          </View>
        </View>
      </TouchableOpacity>
    ),
    [onViewDetail],
  );

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.searchWrapper}>
        <Icon
          tvParallaxProperties={null}
          name="arrow-back"
          size={30}
          color="#ffffff"
          onPress={navigation.goBack}
        />
        <View style={styles.searchBar}>
          <SearchBar placeholder="Search" />
        </View>
      </View>
      <View style={[styles.scrollView]}>
        <FlatList
          data={data || []}
          renderItem={renderItem}
          keyExtractor={keyExtractor}
          style={styles.galleryList}
        />
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#333333',
  },
  scrollView: {
    marginHorizontal: 0,
    backgroundColor: '#f7f7f7',
    height: '100%',
  },
  searchWrapper: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#333333',
    paddingStart: 10,
  },
  searchBar: {
    flex: 1,
  },
  galleryList: {
    marginBottom: 150,
  },
  Gallery: {
    paddingVertical: 15,
    paddingHorizontal: 30,
    marginBottom: 15,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#ffffff',
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
  },
  content: {
    width: '100%',
  },
  mainImage: {
    height: 204,
    width: '100%',
    marginBottom: 10,
  },
  title: {
    fontSize: config.theme.headingFontSize,
    color: config.theme.headingTextColor,
  },
});

export default GalleryScreen;
